import { collection, doc, query, updateDoc, where } from "firebase/firestore";
import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { ErrorText, SBCard, SecondaryButton } from "@/components/ui";
import { db } from "@/firebase/app";
import { useCollection } from "@/hooks/firestoreHooks";
import { parseLeague, parseSport } from "@/models/catalog";
import { USER_ROLE_LABELS } from "@/models/user";
import { bootstrapMemberSummaries, removeLeagueManager, repairLeagueAdmins } from "@/services/admin";
import { useActiveLeagueID, useLeagueSnapshot } from "@/features/home/useLeagueSnapshot";
import "@/features/home/home.css";

// Port of SBotAdminView/SBotAdminHomeView: catalog review (approve/reject
// pending sports & leagues), maintenance endpoints, and staff tools.

export function AdminView() {
  const pendingSports = useCollection(
    query(collection(db, "sports"), where("reviewStatus", "==", "pendingReview")),
    parseSport,
    "admin:pendingSports",
  );
  const pendingLeagues = useCollection(
    query(collection(db, "leagues"), where("reviewStatus", "==", "pendingReview")),
    parseLeague,
    "admin:pendingLeagues",
  );

  const { leagueID } = useActiveLeagueID();
  const snapshot = useLeagueSnapshot(false);
  const navigate = useNavigate();

  const [error, setError] = useState<string | null>(null);
  const [maintenanceLog, setMaintenanceLog] = useState<string | null>(null);
  const [isRunning, setIsRunning] = useState(false);

  const review = async (collectionName: "sports" | "leagues", id: string, reviewStatus: "approved" | "rejected") => {
    setError(null);
    try {
      await updateDoc(doc(db, collectionName, id), { reviewStatus, updatedAt: new Date() });
    } catch (err) {
      setError(err instanceof Error ? err.message : "Review update failed.");
    }
  };

  const runMaintenance = async (label: string, task: () => Promise<object>) => {
    setIsRunning(true);
    setError(null);
    setMaintenanceLog(null);
    try {
      const result = await task();
      setMaintenanceLog(`${label}: ${JSON.stringify(result)}`);
    } catch (err) {
      setError(err instanceof Error ? err.message : `${label} failed.`);
    } finally {
      setIsRunning(false);
    }
  };

  const staff = snapshot.memberSummaries.filter((member) =>
    member.roles.some(
      (role) =>
        role === "admin" ||
        role === "leagueManager" ||
        role === "leagueStaff" ||
        role === "organizationManager" ||
        role === "organizationStaff",
    ),
  );

  return (
    <div className="list-screen">
      <div className="list-screen-head">
        <h1>SBot Labs Admin</h1>
      </div>
      <ErrorText>{error}</ErrorText>

      <SBCard className="home-card">
        <div className="event-row" role="button" tabIndex={0} onClick={() => navigate("/admin/support")} onKeyDown={(event) => event.key === "Enter" && navigate("/admin/support")}>
          <div className="event-row-main">
            <strong>Support Inbox</strong>
            <span>Read and reply to user support requests</span>
          </div>
          <span className="badge">open</span>
        </div>
        <div className="event-row" role="button" tabIndex={0} onClick={() => navigate("/admin/billing")} onKeyDown={(event) => event.key === "Enter" && navigate("/admin/billing")}>
          <div className="event-row-main">
            <strong>Billing</strong>
            <span>AI usage, cost attribution, and platform spend</span>
          </div>
          <span className="badge">open</span>
        </div>
        <div className="event-row" role="button" tabIndex={0} onClick={() => navigate("/admin/league-billing")} onKeyDown={(event) => event.key === "Enter" && navigate("/admin/league-billing")}>
          <div className="event-row-main">
            <strong>League Billing</strong>
            <span>Customer tiers, invoice schedules, and immediate delivery</span>
          </div>
          <span className="badge">manage</span>
        </div>
        <div className="event-row" role="button" tabIndex={0} onClick={() => navigate("/admin/ai-usage")} onKeyDown={(event) => event.key === "Enter" && navigate("/admin/ai-usage")}>
          <div className="event-row-main">
            <strong>AI Usage Limits</strong>
            <span>Free Tier defaults and negotiated paid-league limits</span>
          </div>
          <span className="badge">manage</span>
        </div>
      </SBCard>

      <SBCard className="home-card">
        <div className="home-card-head">
          <h3>Catalog review</h3>
          <span className="badge">{pendingSports.data.length + pendingLeagues.data.length} pending</span>
        </div>
        {pendingSports.data.map((sport) => (
          <div key={sport.id} className="event-row" style={{ cursor: "default" }}>
            <div className="event-row-main">
              <strong>{sport.name}</strong>
              <span>Sport · {sport.slug}</span>
            </div>
            <div className="event-row-badges">
              <SecondaryButton title="Approve" onClick={() => void review("sports", sport.id, "approved")} />
              <SecondaryButton title="Reject" destructive onClick={() => void review("sports", sport.id, "rejected")} />
            </div>
          </div>
        ))}
        {pendingLeagues.data.map((league) => (
          <div key={league.id} className="event-row" style={{ cursor: "default" }}>
            <div className="event-row-main">
              <strong>{league.name}</strong>
              <span>League · sport {league.sportID}</span>
            </div>
            <div className="event-row-badges">
              <SecondaryButton title="Approve" onClick={() => void review("leagues", league.id, "approved")} />
              <SecondaryButton title="Reject" destructive onClick={() => void review("leagues", league.id, "rejected")} />
            </div>
          </div>
        ))}
        {pendingSports.data.length + pendingLeagues.data.length === 0 && <p className="home-hint">Nothing pending review.</p>}
        <ErrorText>{pendingSports.error ?? pendingLeagues.error}</ErrorText>
      </SBCard>

      <SBCard className="home-card">
        <h3>Maintenance</h3>
        <div style={{ display: "flex", gap: 10, flexWrap: "wrap" }}>
          <SecondaryButton
            title="Bootstrap member summaries"
            disabled={isRunning}
            onClick={() => void runMaintenance("Bootstrap", () => bootstrapMemberSummaries())}
          />
          <SecondaryButton
            title="Repair league admins"
            disabled={isRunning}
            onClick={() => void runMaintenance("Repair", () => repairLeagueAdmins())}
          />
        </div>
        {maintenanceLog && <p className="home-hint" style={{ wordBreak: "break-all" }}>{maintenanceLog}</p>}
      </SBCard>

      <SBCard className="home-card">
        <div className="home-card-head">
          <h3>Staff — active league</h3>
          <span className="badge">{leagueID ?? "no league"}</span>
        </div>
        {staff.map((member) => (
          <div key={member.id} className="event-row" style={{ cursor: "default" }}>
            <div className="event-row-main">
              <strong>{member.displayName}</strong>
              <span>{member.roles.map((role) => USER_ROLE_LABELS[role]).join(", ")}</span>
            </div>
            {member.roles.includes("leagueManager") && leagueID && (
              <SecondaryButton
                title="Remove manager role"
                destructive
                onClick={() => void runMaintenance("Remove manager", async () => {
                  await removeLeagueManager(member.userID, leagueID);
                  return { removed: member.userID };
                })}
              />
            )}
          </div>
        ))}
        {staff.length === 0 && <p className="home-hint">No staff summaries in the active league.</p>}
      </SBCard>
    </div>
  );
}
